import { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Clock, AlertTriangle, CheckCircle, Calendar, FileText, Plus, Filter, ChevronDown, ChevronUp, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { useIsMobile } from "@/hooks/use-mobile";

import { AppSidebar } from "@/components/AppSidebar";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useCustomerManagement } from "@/hooks/useCustomerManagement";
import type { Todo, Customer } from "@/types/database";

const TodoManagement = () => {
  const { toast } = useToast();
  const { addProtocolEntry } = useCustomerManagement();
  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();
  const [todos, setTodos] = useState<Todo[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  
  const [loading, setLoading] = useState(true);
  const [selectedTodo, setSelectedTodo] = useState<Todo | null>(null);
  const [isCompletingTodo, setIsCompletingTodo] = useState(false);
  const [isDeletingTodo, setIsDeletingTodo] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [completionComment, setCompletionComment] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("pending");
  const [sortOrder, setSortOrder] = useState<string>("oldest");
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showOverdueOnly, setShowOverdueOnly] = useState(false);
  
  // Create Todo Dialog States
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [isCreatingTodo, setIsCreatingTodo] = useState(false);
  const [newTodoData, setNewTodoData] = useState({
    title: "",
    description: "",
    customer_id: "",
    due_date: "",
    priority: "medium"
  });

  // Initialize sidebar state from localStorage, default to true (expanded)
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('sidebarState');
    return saved ? JSON.parse(saved) : true;
  });

  // Save sidebar state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('sidebarState', JSON.stringify(isSidebarOpen));
  }, [isSidebarOpen]);

  // Load user preferences
  useEffect(() => {
    const loadUserPreferences = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data: profile, error } = await supabase
        .from('profiles')
        .select('ui_preferences')
        .eq('user_id', session.user.id)
        .single();

      if (error) {
        console.error('Error loading user preferences:', error);
      } else if (profile?.ui_preferences && typeof profile.ui_preferences === 'object') {
        const prefs = profile.ui_preferences as any;
        if (typeof prefs.filtersExpanded === 'boolean') {
          setFiltersExpanded(prefs.filtersExpanded);
        }
      }
    };

    loadUserPreferences();
  }, []);

  // Save user preferences when filters state changes
  useEffect(() => {
    const saveUserPreferences = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { error } = await supabase
        .from('profiles')
        .update({ 
          ui_preferences: { filtersExpanded } 
        })
        .eq('user_id', session.user.id);

      if (error) {
        console.error('Error saving user preferences:', error);
      }
    };

    // Only save after initial load to avoid saving default state
    const timeoutId = setTimeout(() => {
      saveUserPreferences();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [filtersExpanded]);

  useEffect(() => {
    const setupRealtimeAndLoadData = async () => {
      const loadTodos = async () => {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (!session) {
            setLoading(false);
            return;
          }

          const { data, error } = await supabase
            .from('todos')
            .select('*')
            .eq('user_id', session.user.id)
            .order('due_date', { ascending: true });

          if (error) {
            console.error('Error loading todos:', error);
          } else {
            setTodos(data || []);
          }
        } catch (error) {
          console.error('Error:', error);
        } finally {
          setLoading(false);
        }
      };

      const loadCustomers = async () => {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (!session) return;

          const { data, error } = await supabase
            .from('customers')
            .select('id, first_name, last_name')
            .eq('user_id', session.user.id)
            .order('first_name', { ascending: true });

          if (error) {
            console.error('Error loading customers:', error);
          } else {
            const customersWithDisplayName = (data || []).map(customer => ({
              id: customer.id,
              first_name: customer.first_name,
              last_name: customer.last_name,
              name: `${customer.first_name} ${customer.last_name}`.trim(),
              user_id: '', 
              created_at: '',
              updated_at: '',
              email: null,
              phone: null,
              address: null,
              notes: null
            }));
            setCustomers(customersWithDisplayName);
          }
        } catch (error) {
          console.error('Error:', error);
        }
      };

      await loadTodos();
      await loadCustomers();

      // Handle navigation state filter
      if (location.state?.filter) {
        handleCardFilter(location.state.filter);
      }

      // Set up realtime subscription for todos
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const channel = supabase
          .channel('todos-management-changes')
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'todos',
              filter: `user_id=eq.${session.user.id}`
            },
            (payload) => {
              console.log('TodoManagement change detected:', payload);
              loadTodos();
            }
          )
          .subscribe();

        return () => {
          supabase.removeChannel(channel);
        };
      }
    };

    setupRealtimeAndLoadData();
  }, []);

  const getDaysUntilDue = (dueDate: string | null) => {
    if (!dueDate) return null;
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getPriorityColor = (daysUntil: number | null) => {
    if (daysUntil === null) return "secondary";
    if (daysUntil < 0) return "destructive";
    if (daysUntil <= 3) return "destructive";
    if (daysUntil <= 7) return "outline";
    return "secondary";
  };

  const getPriorityIcon = (daysUntil: number | null) => {
    if (daysUntil === null) return Clock;
    if (daysUntil < 0) return AlertTriangle;
    if (daysUntil <= 3) return AlertTriangle;
    return Clock;
  };

  const completeTodo = async () => {
    if (!selectedTodo) return;
    
    setIsCompletingTodo(true);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: "Fehler",
          description: "Sie müssen angemeldet sein.",
          variant: "destructive",
        });
        return;
      }

      // Mark todo as completed
      const { error } = await supabase
        .from('todos')
        .update({
          completed: true,
          completed_at: new Date().toISOString(),
          completed_comment: completionComment
        })
        .eq('id', selectedTodo.id);

      if (error) {
        console.error('Error completing todo:', error);
        toast({
          title: "Fehler",
          description: "To-Do konnte nicht abgeschlossen werden.",
          variant: "destructive",
        });
        return;
      }

      // Add protocol entry to customer if customer_id exists
      if (selectedTodo.customer_id) {
        const protocolEntry = `Aufgabe abgeschlossen: ${selectedTodo.title}${completionComment ? ` - ${completionComment}` : ''}`;
        await addProtocolEntry(selectedTodo.customer_id, protocolEntry);
      }

      // Send email notification to customer
      try {
        await supabase.functions.invoke('send-task-notification', {
          body: { todoId: selectedTodo.id, type: 'completed' }
        });
      } catch (emailError) {
        console.error('Error sending task notification email:', emailError);
      }

      // Reset states
      setSelectedTodo(null);
      setCompletionComment("");

      toast({
        title: "Aufgabe abgeschlossen",
        description: "Die Aufgabe wurde erfolgreich abgeschlossen und im Kundenprotokoll dokumentiert.",
      });

    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Fehler",
        description: "Ein unerwarteter Fehler ist aufgetreten.",
        variant: "destructive",
      });
    } finally {
      setIsCompletingTodo(false);
    }
  };

  const deleteTodo = async () => {
    if (!selectedTodo) return;
    
    setIsDeletingTodo(true);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: "Fehler",
          description: "Sie müssen angemeldet sein.",
          variant: "destructive",
        });
        return;
      }

      // Delete todo
      const { error } = await supabase
        .from('todos')
        .delete()
        .eq('id', selectedTodo.id);

      if (error) {
        console.error('Error deleting todo:', error);
        toast({
          title: "Fehler",
          description: "Aufgabe konnte nicht gelöscht werden.",
          variant: "destructive",
        });
        return;
      }

      // Reset states
      setSelectedTodo(null);
      setShowDeleteDialog(false);

      // Remove deleted todo from state
      setTodos(prevTodos => prevTodos.filter(t => t.id !== selectedTodo.id));

      toast({
        title: "Aufgabe gelöscht",
        description: "Die Aufgabe wurde erfolgreich gelöscht.",
      });

    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Fehler",
        description: "Ein unerwarteter Fehler ist aufgetreten.",
        variant: "destructive",
      });
    } finally {
      setIsDeletingTodo(false);
    }
  };

  const createTodo = async () => {
    if (!newTodoData.title || !newTodoData.customer_id) {
      toast({
        title: "Fehler",
        description: "Bitte füllen Sie mindestens Titel und Kunde aus.",
        variant: "destructive",
      });
      return;
    }
    
    setIsCreatingTodo(true);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: "Fehler",
          description: "Sie müssen angemeldet sein.",
          variant: "destructive",
        });
        return;
      }

      const { error } = await supabase
        .from('todos')
        .insert({
          user_id: session.user.id,
          title: newTodoData.title,
          description: newTodoData.description || null,
          customer_id: newTodoData.customer_id,
          due_date: newTodoData.due_date || null,
          priority: newTodoData.priority,
          completed: false
        });

      if (error) {
        console.error('Error creating todo:', error);
        toast({
          title: "Fehler",
          description: "Aufgabe konnte nicht erstellt werden.",
          variant: "destructive",
        });
        return;
      }

      // Reset form
      setNewTodoData({
        title: "",
        description: "",
        customer_id: "",
        due_date: "",
        priority: "medium"
      });
      setShowCreateDialog(false);

      toast({
        title: "Aufgabe erstellt",
        description: "Die Aufgabe wurde erfolgreich erstellt.",
      });

    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Fehler",
        description: "Ein unerwarteter Fehler ist aufgetreten.",
        variant: "destructive",
      });
    } finally {
      setIsCreatingTodo(false);
    }
  };

  const getCustomerName = (customerId: string) => {
    const customer = customers.find(c => c.id === customerId);
    return customer ? customer.name : 'Unbekannter Kunde';
  };

  // Filter todos based on status and search term
  const filteredTodos = todos.filter(todo => {
    const matchesStatus = filterStatus === "all" || 
                         (filterStatus === "completed" && todo.completed) ||
                         (filterStatus === "pending" && !todo.completed) ||
                         (filterStatus === "overdue" && !todo.completed && todo.due_date && getDaysUntilDue(todo.due_date)! < 0);
    
    const matchesSearch = searchTerm === "" || 
                         todo.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (todo.description?.toLowerCase().includes(searchTerm.toLowerCase()));
    
    // Check for overdue filter (legacy support for card filters)
    const matchesOverdue = !showOverdueOnly || 
                          (todo.due_date && getDaysUntilDue(todo.due_date)! < 0 && !todo.completed);
    
    return matchesStatus && matchesSearch && matchesOverdue;
  }).sort((a, b) => {
    // Sort by creation date
    const dateA = new Date(a.created_at).getTime();
    const dateB = new Date(b.created_at).getTime();
    
    if (sortOrder === "newest") {
      return dateB - dateA; // Newest first
    } else {
      return dateA - dateB; // Oldest first
    }
  });

  const handleCardFilter = (filterType: string) => {
    setSearchTerm(""); // Clear search when filtering by card
    setShowOverdueOnly(false); // Reset overdue filter
    
    switch(filterType) {
      case 'all':
        setFilterStatus("all");
        break;
      case 'pending':
        setFilterStatus("pending");
        break;
      case 'overdue':
        setFilterStatus("overdue");
        break;
      case 'completed':
        setFilterStatus("completed");
        break;
    }
  };

  const completedTodos = todos.filter(t => t.completed);
  const pendingTodos = todos.filter(t => !t.completed);
  const overdueTodos = pendingTodos.filter(t => t.due_date && getDaysUntilDue(t.due_date)! < 0);

  if (loading) {
    return (
      <SidebarProvider open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
        <div className="min-h-screen flex w-full bg-gradient-subtle">
          <AppSidebar />
          <div className="flex-1 flex flex-col">
            <header className="h-14 md:h-12 flex items-center border-b bg-card/80 backdrop-blur-sm sticky top-0 z-40 px-2 md:px-0">
              <SidebarTrigger className="ml-2" />
            </header>
            <div className="flex-1 flex items-center justify-center p-4">
              <div className="text-center">
                <Clock className="h-8 w-8 mx-auto mb-4 text-muted-foreground animate-spin" />
                <p className="text-muted-foreground">Lade To-Dos...</p>
              </div>
            </div>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <>
      <SidebarProvider open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
        <div className="min-h-screen flex w-full bg-gradient-subtle">
          <AppSidebar />
          
          <div className="flex-1 flex flex-col min-w-0">
            <header className="h-14 md:h-12 flex items-center border-b bg-card/80 backdrop-blur-sm sticky top-0 z-40 px-2 md:px-0">
              <SidebarTrigger className="ml-2" />
              <div className="flex-1 flex items-center justify-between px-2 md:px-4">
                <h1 className="text-base md:text-lg font-semibold truncate">To-Do Verwaltung</h1>
              </div>
            </header>

            <div className="flex-1 overflow-auto p-3 md:p-6">
              <div className="w-full space-y-4 md:space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                  <Card 
                    className={`cursor-pointer transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] ${
                      filterStatus === 'all' 
                        ? 'bg-muted/80 border-primary/50 shadow-md' 
                        : 'hover:bg-muted/50'
                    }`}
                    onClick={() => handleCardFilter('all')}
                  >
                    <CardContent className="p-3 md:p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs md:text-sm text-muted-foreground">Gesamt</p>
                          <p className="text-xl md:text-2xl font-bold">{todos.length}</p>
                        </div>
                        <FileText className="h-6 w-6 md:h-8 md:w-8 text-muted-foreground" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card 
                    className={`cursor-pointer transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] ${
                      filterStatus === 'pending' 
                        ? 'bg-blue-50 border-blue-300 shadow-md' 
                        : 'hover:bg-blue-50 hover:border-blue-200'
                    }`}
                    onClick={() => handleCardFilter('pending')}
                  >
                    <CardContent className="p-3 md:p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs md:text-sm text-muted-foreground">Offen</p>
                          <p className="text-xl md:text-2xl font-bold text-blue-500">{pendingTodos.length}</p>
                        </div>
                        <Clock className="h-6 w-6 md:h-8 md:w-8 text-blue-500" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card 
                    className={`cursor-pointer transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] ${
                      filterStatus === 'overdue' 
                        ? 'bg-red-50 border-red-300 shadow-md' 
                        : 'hover:bg-red-50 hover:border-red-200'
                    }`}
                    onClick={() => handleCardFilter('overdue')}
                  >
                    <CardContent className="p-3 md:p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs md:text-sm text-muted-foreground">Überfällig</p>
                          <p className="text-xl md:text-2xl font-bold text-red-500">{overdueTodos.length}</p>
                        </div>
                        <AlertTriangle className="h-6 w-6 md:h-8 md:w-8 text-red-500" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card 
                    className={`cursor-pointer transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] ${
                      filterStatus === 'completed' 
                        ? 'bg-green-50 border-green-300 shadow-md' 
                        : 'hover:bg-green-50 hover:border-green-200'
                    }`}
                    onClick={() => handleCardFilter('completed')}
                  >
                    <CardContent className="p-3 md:p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs md:text-sm text-muted-foreground">Abgeschlossen</p>
                          <p className="text-xl md:text-2xl font-bold text-green-500">{completedTodos.length}</p>
                        </div>
                        <CheckCircle className="h-6 w-6 md:h-8 md:w-8 text-green-500" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Filters */}
                <Card>
                  <CardHeader className="pb-3 md:pb-6 cursor-pointer" onClick={() => setFiltersExpanded(!filtersExpanded)}>
                    <CardTitle className="flex items-center justify-between text-base md:text-lg">
                      <div className="flex items-center gap-2">
                        <Filter className="h-4 w-4 md:h-5 md:w-5" />
                        Filter & Suche
                      </div>
                      {filtersExpanded ? (
                        <ChevronUp className="h-4 w-4 md:h-5 md:w-5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-4 w-4 md:h-5 md:w-5 text-muted-foreground" />
                      )}
                    </CardTitle>
                  </CardHeader>
                  {filtersExpanded && (
                     <CardContent className="pt-0">
                       <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                         <div className="space-y-2">
                           <Label className="text-sm md:text-base">Suche</Label>
                           <Input
                             placeholder="Titel oder Beschreibung suchen..."
                             value={searchTerm}
                             onChange={(e) => setSearchTerm(e.target.value)}
                             className="h-10 md:h-9"
                           />
                         </div>
                          
                          <div className="space-y-2">
                            <Label className="text-sm md:text-base">Sortierung</Label>
                            <Select value={sortOrder} onValueChange={setSortOrder}>
                              <SelectTrigger className="h-10 md:h-9">
                                <SelectValue placeholder="Sortierung wählen" />
                              </SelectTrigger>
                               <SelectContent>
                                 <SelectItem value="newest">Neueste</SelectItem>
                                 <SelectItem value="oldest">Älteste</SelectItem>
                               </SelectContent>
                            </Select>
                          </div>
                       </div>
                    </CardContent>
                  )}
                </Card>

                {/* Todo List */}
                <Card>
                  <CardHeader className="pb-3 md:pb-6">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base md:text-lg">To-Do Liste ({filteredTodos.length})</CardTitle>
                      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                        <DialogTrigger asChild>
                          <Button size="sm" className="h-8">
                            <Plus className="h-4 w-4 mr-2" />
                            Neue Aufgabe
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-md">
                          <DialogHeader>
                            <DialogTitle>Neue Aufgabe erstellen</DialogTitle>
                            <DialogDescription>
                              Erstellen Sie eine neue Aufgabe für einen Kunden.
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4 py-4">
                            <div className="space-y-2">
                              <Label htmlFor="customer">Kunde *</Label>
                              <Select 
                                value={newTodoData.customer_id} 
                                onValueChange={(value) => setNewTodoData({...newTodoData, customer_id: value})}
                              >
                                <SelectTrigger id="customer">
                                  <SelectValue placeholder="Kunde auswählen" />
                                </SelectTrigger>
                                <SelectContent>
                                  {customers.map((customer) => (
                                    <SelectItem key={customer.id} value={customer.id}>
                                      {customer.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            
                            <div className="space-y-2">
                              <Label htmlFor="title">Titel *</Label>
                              <Input
                                id="title"
                                value={newTodoData.title}
                                onChange={(e) => setNewTodoData({...newTodoData, title: e.target.value})}
                                placeholder="Aufgabentitel eingeben"
                              />
                            </div>
                            
                            <div className="space-y-2">
                              <Label htmlFor="description">Beschreibung</Label>
                              <Textarea
                                id="description"
                                value={newTodoData.description}
                                onChange={(e) => setNewTodoData({...newTodoData, description: e.target.value})}
                                placeholder="Aufgabenbeschreibung eingeben"
                                rows={3}
                              />
                            </div>
                            
                            <div className="space-y-2">
                              <Label htmlFor="due_date">Fälligkeitsdatum</Label>
                              <Input
                                id="due_date"
                                type="date"
                                value={newTodoData.due_date}
                                onChange={(e) => setNewTodoData({...newTodoData, due_date: e.target.value})}
                              />
                            </div>
                            
                            <div className="space-y-2">
                              <Label htmlFor="priority">Priorität</Label>
                              <Select 
                                value={newTodoData.priority} 
                                onValueChange={(value) => setNewTodoData({...newTodoData, priority: value})}
                              >
                                <SelectTrigger id="priority">
                                  <SelectValue placeholder="Priorität wählen" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="low">Niedrig</SelectItem>
                                  <SelectItem value="medium">Mittel</SelectItem>
                                  <SelectItem value="high">Hoch</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              onClick={() => setShowCreateDialog(false)}
                              disabled={isCreatingTodo}
                            >
                              Abbrechen
                            </Button>
                            <Button
                              onClick={createTodo}
                              disabled={isCreatingTodo}
                            >
                              {isCreatingTodo ? "Wird erstellt..." : "Erstellen"}
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                    <CardDescription className="text-sm md:text-base">
                      Verwalten Sie alle Ihre Aufgaben an einem Ort
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {filteredTodos.length > 0 ? (
                      <div className="space-y-3">
                        {filteredTodos.map((todo) => {
                          const daysUntil = getDaysUntilDue(todo.due_date);
                          const PriorityIcon = getPriorityIcon(daysUntil);
                          
                          return (
                             <div 
                               key={todo.id} 
                               className={`p-4 md:p-4 border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer touch-manipulation ${
                                 todo.completed ? 'border-l-4 border-l-green-500' : ''
                               }`}
                               onClick={() => setSelectedTodo(todo)}
                             >
                               <div className="flex flex-col gap-3 md:flex-row md:items-center justify-between md:gap-2">
                                 <div className="flex items-start md:items-center gap-3 flex-1 min-w-0">
                                   {todo.completed ? (
                                     <CheckCircle className="h-5 w-5 text-green-500" />
                                   ) : (
                                     <PriorityIcon className={`h-5 w-5 ${
                                       daysUntil !== null && daysUntil < 0 ? 'text-red-500' : 
                                       daysUntil !== null && daysUntil <= 3 ? 'text-red-500' : 
                                       'text-muted-foreground'
                                     }`} />
                                   )}
                                   
                                   <div className="flex-1 min-w-0">
                                     {todo.customer_id && (
                                       <p className="text-sm md:text-xs font-medium truncate">
                                         {getCustomerName(todo.customer_id)}
                                       </p>
                                     )}
                                     <div className="space-y-1">
                                       <p className="text-xs text-muted-foreground font-medium">Aufgabe:</p>
                                        <h3 className={`font-medium text-xs md:text-sm leading-relaxed ${todo.completed ? 'line-through text-muted-foreground' : ''}`}>
                                          {todo.title.includes(':') ? todo.title.split(':').slice(1).join(':').trim() : todo.title}
                                        </h3>
                                     </div>
                                     {todo.description && (
                                       <p className="text-sm md:text-sm text-muted-foreground leading-relaxed truncate">
                                         {todo.description}
                                       </p>
                                     )}
                                   </div>
                                 </div>
                                
                                 <div className="flex items-center gap-2 flex-wrap md:flex-shrink-0">
                                   {todo.due_date && (
                                     <Badge variant={getPriorityColor(daysUntil)} className="text-xs md:text-xs whitespace-nowrap">
                                       {daysUntil !== null && daysUntil < 0 ? `${Math.abs(daysUntil)}T überfällig` : 
                                        daysUntil === 0 ? 'Heute' :
                                       daysUntil === 1 ? 'Morgen' :
                                       daysUntil !== null ? `${daysUntil} Tage` : ''}
                                    </Badge>
                                   )}
                                   
                                   {todo.completed && (
                                     <Badge variant="secondary" className="text-xs md:text-xs">
                                       Abgeschlossen
                                     </Badge>
                                   )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-8 px-4">
                        <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                        <p className="text-base md:text-lg font-medium mb-2">Keine To-Dos gefunden</p>
                        <p className="text-sm md:text-base text-muted-foreground">
                          {searchTerm || filterStatus !== "all"
                            ? "Versuchen Sie andere Filter oder Suchbegriffe"
                            : "Erstellen Sie Ihr erstes To-Do auf der Startseite"
                          }
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </SidebarProvider>

      {/* Todo Detail Dialog */}
      <Dialog open={!!selectedTodo} onOpenChange={(open) => !open && setSelectedTodo(null)}>
        <DialogContent className="mx-2 w-[calc(100vw-16px)] max-w-[425px] md:mx-4 md:max-w-lg max-h-[90vh] overflow-y-auto">
          {selectedTodo && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-base md:text-lg">
                  <FileText className="h-4 w-4 md:h-5 md:w-5" />
                  Aufgabe Details
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4 md:space-y-6">
                <div className="space-y-4 md:space-y-3">
                  <div>
                    <Label className="text-sm md:text-sm font-medium">Aufgabe</Label>
                    <p className="text-sm md:text-sm text-muted-foreground mt-1 leading-relaxed">{selectedTodo.title}</p>
                  </div>

                  {selectedTodo.customer_id && (
                    <div>
                      <Label className="text-sm md:text-sm font-medium">Kunde</Label>
                      <Link to={`/customers?customerId=${selectedTodo.customer_id}`}>
                        <p className="text-sm md:text-sm text-muted-foreground hover:text-primary cursor-pointer underline mt-1">
                          {getCustomerName(selectedTodo.customer_id)}
                        </p>
                      </Link>
                    </div>
                  )}

                  {selectedTodo.description && (
                    <div>
                      <Label className="text-sm md:text-sm font-medium">Notiz</Label>
                      <p className="text-sm md:text-sm text-muted-foreground mt-1 leading-relaxed">{selectedTodo.description}</p>
                    </div>
                  )}


                  {selectedTodo.due_date && (
                    <div>
                      <Label className="text-sm md:text-sm font-medium">Fälligkeitsdatum</Label>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-1">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 flex-shrink-0" />
                          <p className="text-sm md:text-sm text-muted-foreground">
                            {new Date(selectedTodo.due_date).toLocaleDateString('de-DE')}
                          </p>
                        </div>
                        <Badge variant={getPriorityColor(getDaysUntilDue(selectedTodo.due_date))} className="text-xs w-fit">
                          {(() => {
                            const daysUntil = getDaysUntilDue(selectedTodo.due_date);
                            return daysUntil !== null && daysUntil < 0 ? `${Math.abs(daysUntil)} Tage überfällig` : 
                                   daysUntil === 0 ? 'Heute' :
                                   daysUntil === 1 ? 'Morgen' :
                                   daysUntil !== null ? `${daysUntil} Tage` : '';
                          })()}
                        </Badge>
                      </div>
                    </div>
                  )}


                  <div>
                    <Label className="text-sm md:text-sm font-medium">Erstellt am</Label>
                    <p className="text-sm md:text-sm text-muted-foreground mt-1">
                      {new Date(selectedTodo.created_at).toLocaleDateString('de-DE', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>

                  {selectedTodo.completed && selectedTodo.completed_at && (
                    <div>
                      <Label className="text-sm md:text-sm font-medium">Abgeschlossen am</Label>
                      <p className="text-sm md:text-sm text-muted-foreground mt-1">
                        {new Date(selectedTodo.completed_at).toLocaleDateString('de-DE', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  )}

                  {selectedTodo.completed && selectedTodo.completed_comment && (
                    <div>
                      <Label className="text-sm md:text-sm font-medium">Abschluss-Kommentar</Label>
                      <p className="text-sm md:text-sm text-muted-foreground mt-1 leading-relaxed">{selectedTodo.completed_comment}</p>
                    </div>
                  )}
                </div>

                {!selectedTodo.completed && (
                  <>
                    <Separator />
                    <div className="space-y-3">
                      <Label className="text-sm md:text-sm font-medium">Abschluss-Kommentar (optional)</Label>
                      <Textarea
                        value={completionComment}
                        onChange={(e) => setCompletionComment(e.target.value)}
                        placeholder="Notizen zum Abschluss der Aufgabe..."
                        rows={3}
                        className="resize-none"
                      />
                    </div>
                  </>
                )}

                <div className="flex flex-col gap-3 pt-4">
                  <div className="flex flex-col gap-3 md:flex-row md:justify-end md:gap-2">
                    <Button 
                      variant="destructive" 
                      onClick={() => setShowDeleteDialog(true)}
                      className="gap-2 h-10 md:h-9 text-sm md:text-sm touch-manipulation"
                    >
                      <Trash2 className="h-4 w-4" />
                      Löschen
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => setSelectedTodo(null)} 
                      className="h-10 md:h-9 text-sm md:text-sm touch-manipulation"
                    >
                      Schließen
                    </Button>
                    {!selectedTodo.completed && (
                      <Button 
                        onClick={completeTodo} 
                        disabled={isCompletingTodo}
                        className="gap-2 h-10 md:h-9 text-sm md:text-sm touch-manipulation"
                      >
                        <CheckCircle className="h-4 w-4" />
                        {isCompletingTodo ? 'Wird abgeschlossen...' : 'Abschließen'}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Aufgabe löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Sind Sie sicher, dass Sie diese Aufgabe löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingTodo}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteTodo}
              disabled={isDeletingTodo}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeletingTodo ? 'Wird gelöscht...' : 'Löschen'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default TodoManagement;